import { RefObject } from 'react';
import dynamic from 'next/dynamic';
import styles from '../../styles/region/RegionTitleBlock.module.scss';
import { RegionSettings, Settings } from '../../data/api';
import { MetricsBySlug } from '../metrics/use-metrics';
import { unitsShorthand } from './MetricTable';
import { useLinearUnitsInfo } from '../../utils/use-linear-units';
import { convertLinearUnitsForDisplay } from '../../utils/units-conversion';
import useTippy from '../../utils/use-tippy';

const RegionMap = dynamic(() => import('../../components/region/RegionMap'), {
    ssr: false,
});

type TitleBlockStats =
    | 'population'
    | 'totalTrips'
    | 'totalDistance'
    | 'averageVehicles';

const Tooltips: Record<
    TitleBlockStats,
    {
        tooltipText: string;
    }
> = {
    population: {
        tooltipText:
            'Population data is from Census Reporter, Australian Bureau of Statistics and NZ Stats.',
    },
    totalTrips: {
        tooltipText: 'Total shared vehicle trips for the time period selected.',
    },
    totalDistance: {
        tooltipText:
            'Total distance ridden by shared vehicles for the time period selected.',
    },
    averageVehicles: {
        tooltipText:
            'Average number of active shared vehicles for the time period selected. Active vehicles refer to vehicles that are available for rent or rented.',
    },
};

interface TitleStatProps {
    label: string;
    stat: string;
    tooltipText: string;
}

function TitleStat({ label, stat, tooltipText }: TitleStatProps) {
    const [headerRef] = useTippy<HTMLDivElement>(
        tooltipText ? tooltipText : null,
        { placement: 'bottom' }
    );
    return (
        <div ref={headerRef} className={styles.titleStat}>
            <span className={styles.label}>{label}</span>
            <span className={styles.stat}>{stat}</span>
        </div>
    );
}

interface RegionTitleBlockProps {
    citySettings: { [slug: string]: Settings };
    metrics: MetricsBySlug | null;
    regionSettings: RegionSettings;
    connectedCitiesRef: RefObject<HTMLDivElement>;
}

function RegionTitleBlock({
    citySettings,
    metrics,
    regionSettings,
    connectedCitiesRef,
}: RegionTitleBlockProps) {
    const linearUnits = useLinearUnitsInfo();
    const numConnectedCities = Object.keys(citySettings).length;
    const totalTrips = Object.values(metrics ?? {})
        .map(m => m?.totalTripCount ?? 0)
        .reduce((partialSum, a) => partialSum + a, 0);
    // If there's not metrics data to show, use the user-specified linear units
    const metricLinearUnits =
        Object.values(metrics ?? {})[0]?.tripDistance.xUnit ??
        linearUnits.units;
    const totalDistance = convertLinearUnitsForDisplay(
        Object.values(metrics ?? {})
            .map(m => m?.tripDistance.sumTotal ?? 0)
            .reduce((partialSum, a) => partialSum + a, 0),
        metricLinearUnits,
        linearUnits
    );
    const avgVehicles = Object.values(metrics ?? {})
        .map(m => m?.meanActiveVehiclesPerDay.average ?? 0)
        .reduce((partialSum, a) => partialSum + a, 0);

    return (
        <div className={styles.container}>
            <div className={styles.titleBubble}>
                <h1 className={styles.title}>{regionSettings.name}</h1>
            </div>
            <div className={styles.regionInfoRow}>
                <a
                    className={styles.connectedCities}
                    onClick={() =>
                        connectedCitiesRef.current?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                            inline: 'nearest',
                        })
                    }
                >{`${numConnectedCities} Connected cities`}</a>
                {regionSettings.organization_logo_url && (
                    <img
                        alt="agency logo"
                        src={regionSettings.organization_logo_url}
                        className={styles.organizationLogo}
                    />
                )}
                <div className={styles.regionMap}>
                    <RegionMap
                        settings={regionSettings}
                        citySettings={citySettings}
                    />
                </div>
            </div>
            <div className={styles.titleStatRow}>
                <TitleStat
                    label="Population"
                    stat={regionSettings.population.toLocaleString()}
                    tooltipText={Tooltips.population.tooltipText}
                />
                <TitleStat
                    label="Total Trips"
                    stat={totalTrips.toLocaleString()}
                    tooltipText={Tooltips.totalTrips.tooltipText}
                />
                <TitleStat
                    label={`Total Distance (${
                        unitsShorthand[linearUnits.units]
                    })`}
                    stat={totalDistance.toLocaleString(undefined, {
                        maximumFractionDigits: 0,
                    })}
                    tooltipText={Tooltips.totalDistance.tooltipText}
                />
                <TitleStat
                    label="Average Vehicles"
                    stat={avgVehicles.toLocaleString(undefined, {
                        maximumFractionDigits: 0,
                    })}
                    tooltipText={Tooltips.averageVehicles.tooltipText}
                />
            </div>
        </div>
    );
}

export default RegionTitleBlock;
